/**
 * Часовой пояс абонента по названию региона из реестра Роскомнадзора (§9.5).
 *
 * Реестр пишет один и тот же субъект по-разному: «Свердловская обл.» и
 * «Свердловская область», «Чеченская Республика» и «Республика Чеченская»,
 * «Ханты - Мансийский - Югра АО» и «Ханты-Мансийский автономный округ - Югра».
 * Только у мобильных диапазонов таких написаний 158 при 85 реальных субъектах.
 * Поэтому сравнение идёт не по строке, а по ключевому слову после нормализации.
 *
 * Пояс — в минутах от UTC, а не именем зоны: смещения регионов России не
 * переводятся на летнее время с 2014 года, и постоянное число не тянет за собой
 * базу зон в браузер.
 *
 * Точность на уровне субъекта. Республика Саха занимает три пояса, Красноярский
 * край — два; берётся пояс административного центра. Ошибка в пределах часа
 * для «звонить или рано» роли не играет, а точнее реестр и не позволяет:
 * координат в нём нет.
 */
const H = 60;
/**
 * Ключевое слово субъекта → смещение. Ключ — то, что осталось от названия
 * после нормализации: без «обл.», «область», «республика», «край», «АО»,
 * дефисов и регистра. Порядок в объекте роли не играет, совпадение ищется
 * по самому длинному ключу — иначе «алтай» перебил бы «алтайский».
 */
const SUBJECT_TZ = {
    // UTC+2
    калининградская: 2 * H,
    // UTC+4
    самарская: 4 * H,
    саратовская: 4 * H,
    ульяновская: 4 * H,
    удмуртская: 4 * H,
    удмуртия: 4 * H,
    астраханская: 4 * H,
    // UTC+5
    башкортостан: 5 * H,
    курганская: 5 * H,
    оренбургская: 5 * H,
    пермский: 5 * H,
    пермская: 5 * H,
    свердловская: 5 * H,
    тюменская: 5 * H,
    челябинская: 5 * H,
    'ханты мансийский': 5 * H,
    ханты: 5 * H,
    югра: 5 * H,
    'ямало ненецкий': 5 * H,
    ямало: 5 * H,
    // UTC+6
    омская: 6 * H,
    // UTC+7
    алтайский: 7 * H,
    алтай: 7 * H,
    кемеровская: 7 * H,
    кузбасс: 7 * H,
    красноярский: 7 * H,
    новосибирская: 7 * H,
    томская: 7 * H,
    хакасия: 7 * H,
    тыва: 7 * H,
    // UTC+8
    бурятия: 8 * H,
    иркутская: 8 * H,
    // UTC+9
    забайкальский: 9 * H,
    амурская: 9 * H,
    саха: 9 * H,
    якутия: 9 * H,
    // UTC+10
    приморский: 10 * H,
    хабаровский: 10 * H,
    еврейская: 10 * H,
    // UTC+11
    сахалинская: 11 * H,
    магаданская: 11 * H,
    // UTC+12
    камчатский: 12 * H,
    чукотский: 12 * H,
};
/** Москва: и пояс большей части страны, и запасной вариант для незнакомого субъекта */
export const MSK_OFFSET_MIN = 3 * H;
/**
 * Регионы, которых нет в карте, — это UTC+3 (европейская часть). Их около
 * полусотни, перечислять каждый незачем: пояс у них один, а список субъектов
 * меняется чаще, чем их часовые пояса.
 *
 * Но «не нашли субъект» и «субъект в UTC+3» — разные вещи, и различать их
 * обязательно: у первого номера пояс неизвестен, и придерживать его по
 * московскому окну — решение админа, а не факт.
 */
const KNOWN_MSK = [
    'москва', 'московская', 'санкт петербург', 'петербург', 'ленинградская',
    'адыгея', 'дагестан', 'ингушетия', 'кабардино', 'калмыкия', 'карачаево',
    'карелия', 'коми', 'крым', 'севастополь', 'марий', 'мордовия', 'осетия',
    'алания', 'татарстан', 'чеченская', 'чечня', 'чувашская', 'чувашия',
    'краснодарский', 'ставропольский', 'архангельская', 'белгородская',
    'брянская', 'владимирская', 'волгоградская', 'вологодская', 'воронежская',
    'ивановская', 'калужская', 'кировская', 'костромская', 'курская',
    'липецкая', 'мурманская', 'ненецкий', 'нижегородская', 'новгородская',
    'орловская', 'пензенская', 'псковская', 'ростовская', 'рязанская',
    'смоленская', 'тамбовская', 'тверская', 'тульская', 'ярославская',
    'донецкая', 'луганская', 'запорожская', 'херсонская',
];
/**
 * Приводит запись реестра к сравнимому виду: нижний регистр, без юридических
 * слов и знаков. «Ханты - Мансийский - Югра АО» → «ханты мансийский югра».
 */
export function normalizeRegionName(raw) {
    // Юридические слова вырезаются целым словом. `\b` для этого не годится:
    // в JavaScript граница слова определена через [A-Za-z0-9_], кириллица в неё
    // не входит, и `\bобл\b` не совпадает никогда — «Свердловская обл.» так и
    // осталась бы «свердловская обл», а «Свердловская область» стала бы другим
    // ключом. Отсюда просмотры по \p{L} с флагом u
    const WORDS = [
        'обл', 'область', 'области', 'край', 'края', 'республика', 'республики',
        'автономный', 'автономная', 'автономного', 'округ', 'округа', 'ао', 'г', 'го', 'рн',
    ];
    const cut = new RegExp(`(?<!\\p{L})(?:${WORDS.join('|')})(?!\\p{L})`, 'gu');
    return raw
        .toLowerCase()
        .replace(/ё/g, 'е')
        // Реестр иногда пишет «г. Псков|Псковская обл.» — субъект справа от черты
        .replace(/^.*\|/, '')
        .replace(/[«»"'()\/*.,]/g, ' ')
        .replace(/[-–—]/g, ' ')
        .replace(cut, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Смещение пояса по названию региона. `null` — субъект не опознан: в реестре
 * стоит «-», «Российская Федерация» или запись, которой нет в карте.
 */
export function tzOffsetByRegion(raw) {
    if (raw === null || raw === undefined)
        return null;
    const name = normalizeRegionName(raw);
    if (name === '' || name === '-')
        return null;
    // Сначала длинные ключи: «ханты мансийский» должен победить «ханты»,
    // а «алтайский» — «алтай», иначе край и республика склеятся в один пояс
    const keys = Object.keys(SUBJECT_TZ).sort((a, b) => b.length - a.length);
    for (const key of keys) {
        if (name.includes(key))
            return SUBJECT_TZ[key] ?? null;
    }
    for (const key of KNOWN_MSK) {
        if (name.includes(key))
            return MSK_OFFSET_MIN;
    }
    return null;
}
/** Местное время абонента: часы 0–23 по его поясу. */
export function localHourAt(now, tzOffsetMin) {
    const shifted = new Date(now.getTime() + tzOffsetMin * 60_000);
    return shifted.getUTCHours();
}
/**
 * Можно ли сейчас звонить абоненту.
 *
 * Границы задаются часами включительно-исключительно: 9 и 21 означают
 * «с 9:00 до 20:59». Час равный началу — уже можно, час равный концу — уже
 * нельзя, иначе «до 21» читалось бы как «до 21:59».
 */
export function isWithinCallingWindow(now, tzOffsetMin, startHour, endHour) {
    const hour = localHourAt(now, tzOffsetMin);
    // Окно через полночь (например с 21 до 9) — это ночной обзвон, но запретить
    // его здесь нельзя: настройку задаёт админ, а наше дело посчитать
    if (startHour <= endHour)
        return hour >= startHour && hour < endHour;
    return hour >= startHour || hour < endHour;
}
/**
 * Все часовые пояса России в минутах: от UTC+2 (Калининград) до UTC+12
 * (Камчатка, Чукотка). Список закрытый и короткий — на этом держится фильтр
 * окна звонка: вместо вычисления часа для каждой строки очереди считается
 * список разрешённых сейчас поясов, и запрос сводится к сравнению с индексом.
 */
export const RU_TZ_OFFSETS = Array.from({ length: 11 }, (_unused, i) => (i + 2) * H);
/**
 * Какие пояса сейчас внутри окна звонка.
 *
 * Считается один раз на запуск раздачи, а не для каждого контакта: поясов
 * одиннадцать, а строк очереди десятки тысяч.
 */
export function allowedTzOffsets(now, startHour, endHour) {
    return RU_TZ_OFFSETS.filter((off) => isWithinCallingWindow(now, off, startHour, endHour));
}
/** Подпись пояса для человека: «UTC+7», «МСК+4». */
export function formatTzOffset(tzOffsetMin) {
    if (tzOffsetMin === null)
        return 'пояс не определён';
    const hours = Math.round(tzOffsetMin / 60);
    const fromMsk = hours - 3;
    const utc = `UTC${hours >= 0 ? '+' : ''}${hours}`;
    if (fromMsk === 0)
        return `${utc} · Москва`;
    return `${utc} · МСК${fromMsk > 0 ? '+' : ''}${fromMsk}`;
}
//# sourceMappingURL=phone-region.js.map